import { EmptyState, type EmptyStateProps, Spinner } from '@/design-system';
import { type UseCryptoAssetsResult } from '@/shared/modules/wallet/use-crypto-assets';
import { UserSuiWalletStatus } from '@/shared/modules/wallet/use-privy-wallet';

import { Centered, StateCard, TableWrapper } from './crypto-assets.styled';
import { CryptoAssetsMobile } from './crypto-assets-mobile';
import { CryptoAssetsTable } from './crypto-assets-table';
import { CryptoPerformanceSelection } from './types';
import { useCryptoAssetsViewState } from './use-crypto-assets-view-state';

interface CryptoAssetsContentProps {
  wallet: UseCryptoAssetsResult;
  isMobile: boolean;
  activePositionId?: string;
  onSelectPosition: (_selection: CryptoPerformanceSelection | null) => void;
  onSetUpWallet: () => void;
}

const StateMessage = (props: EmptyStateProps) => (
  <StateCard>
    <EmptyState {...props} />
  </StateCard>
);

export const CryptoAssetsContent = ({
  wallet,
  isMobile,
  activePositionId,
  onSelectPosition,
  onSetUpWallet,
}: CryptoAssetsContentProps) => {
  const { assets, isLoading, isWalletError, walletStatus, suiAddress } = wallet;
  const view = useCryptoAssetsViewState(assets);

  if (walletStatus === UserSuiWalletStatus.NOT_STARTED) {
    return (
      <StateMessage
        variant="tertiary"
        title="Set up a wallet with a passkey when you are ready."
        action={{ label: 'Set up wallet', onClick: onSetUpWallet }}
      />
    );
  }

  if (isLoading) {
    return (
      <Centered role="status" aria-label="Loading crypto assets">
        <Spinner />
      </Centered>
    );
  }

  if (isWalletError) {
    return (
      <StateMessage
        variant="tertiary"
        showIcon={false}
        title="We couldn't prepare your wallet. Please reload and try again."
      />
    );
  }

  if (isMobile) {
    return (
      <CryptoAssetsMobile
        assets={assets}
        sortedAssets={view.sortedAssets}
        activePositionId={activePositionId}
        suiAddress={suiAddress}
        onSelectPosition={onSelectPosition}
      />
    );
  }

  if (assets.length === 0) {
    return <StateMessage variant="tertiary" title="No crypto assets found for this wallet." />;
  }

  return (
    <TableWrapper>
      <CryptoAssetsTable
        assets={assets}
        sortedAssets={view.sortedAssets}
        sortKey={view.sortKey}
        sortDir={view.sortDir}
        activePositionId={activePositionId}
        suiAddress={suiAddress}
        onSort={view.handleSort}
        onSelectPosition={onSelectPosition}
      />
    </TableWrapper>
  );
};
